import { useState } from "react";
import ScoreGauge from "./ScoreGauge";

interface Check {
  label: string;
  status: "pass" | "warn" | "fail" | "info";
  detail?: string;
}

interface Props {
  title: string;
  score: number;
  summary: string;
  checks: Check[];
  comingSoon?: boolean;
}

function statusBadge(status: Check["status"]) {
  const map = {
    pass: "bg-green-100 text-green-700",
    warn: "bg-amber-100 text-amber-700",
    fail: "bg-red-100 text-red-700",
    info: "bg-ink-100 text-ink-600",
  } as const;
  const label = {
    pass: "Pass",
    warn: "Partial",
    fail: "Fail",
    info: "Info",
  }[status];
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${map[status]}`}
    >
      {label}
    </span>
  );
}

export default function CategoryCard({
  title,
  score,
  summary,
  checks,
  comingSoon = false,
}: Props) {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-xl border border-ink-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <div className="flex items-center gap-4">
          {!comingSoon ? (
            <ScoreGauge score={score} size="sm" />
          ) : (
            <div className="flex h-24 w-24 items-center justify-center rounded-full border border-dashed border-ink-200 text-ink-400 text-xs">
              Phase 2
            </div>
          )}
          <div>
            <h3 className="text-lg font-semibold text-ink-900">{title}</h3>
            <p className="text-ink-500 mt-1 text-sm">{summary}</p>
          </div>
        </div>
        <div className="text-ink-400">{open ? "▾" : "▸"}</div>
      </button>
      {open && (
        <ul className="border-t border-ink-100 divide-y divide-ink-100">
          {checks.length === 0 ? (
            <li className="px-5 py-4 text-ink-500 text-sm">No checks reported.</li>
          ) : (
            checks.map((c, idx) => (
              <li key={idx} className="px-5 py-3 flex items-start gap-3">
                <div className="pt-0.5">{statusBadge(c.status)}</div>
                <div>
                  <div className="text-ink-900 text-sm font-medium">{c.label}</div>
                  {c.detail && (
                    <div className="text-ink-500 text-sm mt-0.5">{c.detail}</div>
                  )}
                </div>
              </li>
            ))
          )}
        </ul>
      )}
    </section>
  );
}
